/**
 * Excel-style sort + filter header for the S&P ledger tables.
 *
 * Looks and behaves like the Sample Tracker's column menu and reuses its `.th-menu` styling, which is global
 * because that panel is portalled to <body>. One thing works differently underneath, and it matters:
 *
 *   The Sample Tracker builds its value list in the BROWSER, because it holds every row. This table holds 20
 *   rows out of 29,937, so a browser-built list would offer only the values on the current page and silently
 *   hide the rest. The list is therefore fetched from the server per column, with the other active filters
 *   applied but not this column's own — the same rule Excel follows, so unticking a value does not make it
 *   disappear from the list you unticked it in.
 *
 * The panel is fixed-positioned from the button's rect rather than absolutely positioned, because the table
 * lives in a scrolling wrapper that would clip it.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpAZ, ArrowDownZA, Filter, FilterX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function FilterPanel({ column, label, anchor, selected, sortDir, loading, values, truncated, onSort, onApply, onClose }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  // No selection means "everything", so the draft starts with every value ticked — otherwise opening a
  // never-filtered column would show an empty tick list and OK would filter the column down to nothing.
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (loading) return;
    setDraft(new Set(selected && selected.length ? selected : values));
  }, [loading, values, selected]);

  const term = search.trim().toLowerCase();
  const shown = term ? values.filter((v) => String(v).toLowerCase().includes(term)) : values;
  const ticked = draft || new Set();
  const allShownTicked = shown.length > 0 && shown.every((v) => ticked.has(v));

  const setMany = (list, on) => setDraft((prev) => {
    const next = new Set(prev || []);
    list.forEach((v) => { if (on) next.add(v); else next.delete(v); });
    return next;
  });

  return createPortal((
    <>
      {/* A click anywhere else closes the panel; it is a sibling of the panel so the panel's own clicks
          never reach it. */}
      <div className="th-menu-backdrop" onClick={onClose} />
      <div
        className="th-menu"
        style={{ top: anchor.top, bottom: anchor.bottom, left: anchor.left }}
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className={`th-menu-sort ${sortDir === 'asc' ? 'on' : ''}`} onClick={() => onSort('asc')}>
          <ArrowUpAZ size={13} /> {t('xHub.ledger.sortAZ')}
        </button>
        <button type="button" className={`th-menu-sort ${sortDir === 'desc' ? 'on' : ''}`} onClick={() => onSort('desc')}>
          <ArrowDownZA size={13} /> {t('xHub.ledger.sortZA')}
        </button>
        <div className="th-menu-sep" />
        <input
          className="th-menu-search"
          autoFocus
          placeholder={t('xHub.ledger.searchCol', { label })}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {loading ? (
          <div className="th-menu-empty">{t('xHub.ledger.loadingValues')}</div>
        ) : (
          <>
            <label className="th-menu-opt th-menu-all">
              <input type="checkbox" checked={allShownTicked} onChange={() => setMany(shown, !allShownTicked)} />
              <span>{t('xHub.ledger.selectAll')}</span>
            </label>
            <div className="th-menu-list" style={{ maxHeight: Math.max(90, Math.min(anchor.space - 170, 300)) }}>
              {shown.map((v) => (
                <label className="th-menu-opt" key={v}>
                  <input type="checkbox" checked={ticked.has(v)} onChange={() => setMany([v], !ticked.has(v))} />
                  <span>{v}</span>
                </label>
              ))}
              {!shown.length && <div className="th-menu-empty">{t('xHub.ledger.noMatches')}</div>}
            </div>
            {truncated && (
              // Said out loud rather than hidden: with the list cut short, "(Select all)" does not mean all.
              <div className="th-menu-empty">{t('xHub.ledger.truncated')}</div>
            )}
          </>
        )}
        <div className="th-menu-foot">
          <button type="button" className="th-menu-btn" onClick={() => onApply([])}>{t('xHub.ledger.clear')}</button>
          <button type="button" className="th-menu-btn" onClick={onClose}>{t('xHub.ledger.cancel')}</button>
          <button
            type="button"
            className="th-menu-btn primary"
            disabled={loading}
            // Everything ticked is the same as no filter at all, and storing it as a filter would leave the
            // funnel looking active for a column that is not filtering anything.
            onClick={() => onApply(ticked.size === values.length ? [] : [...ticked])}
          >
            {t('xHub.ledger.ok')}
          </button>
        </div>
      </div>
    </>
  ), document.body);
}

/** Which columns freeze: the grey identity block, and only while it is a contiguous run from the left. */
export const SP_STICKY_COLS = new Set([
  'so_line_id', 'po_line_id', 'ns_line_id', 'line_status', 'superseded_by', 'batch_id', 'last_import_at',
]);

/** The frozen run: leading columns only. A grey column further right does not freeze. */
export const spStickyRun = (columns) => {
  const run = [];
  for (const c of columns) {
    if (!SP_STICKY_COLS.has(c)) break;
    run.push(c);
  }
  return run;
};

/**
 * Frozen-column widths, ENFORCED rather than measured.
 *
 * Measuring and accumulating was the wrong approach twice over. Real column widths are fractional (103.83,
 * 104.72, 128.75 …); summing them raw left every frozen cell 0.2-0.3px right of its column, and a sub-pixel
 * sliver on the left edge is a window onto the scrolling content — the hairline of moving text Max saw.
 * Rounding the widths did not fix it either: the LEFT became an integer while the cell's own width stayed
 * fractional, so the seam simply moved.
 *
 * Declaring the widths in a <colgroup> and letting the cells clip (max-width: 0 + overflow hidden, the standard
 * table-ellipsis trick) makes the rendered width exactly the declared integer. The offsets are then plain
 * arithmetic with nothing left to drift.
 */
export const SP_STICKY_WIDTH = {
  so_line_id: 108, po_line_id: 108, ns_line_id: 108,
  // Widened 2026-08-27 (Max): at 96/116 the two labels were clipped to "Line_Status." and "Superseded_By.".
  line_status: 120, superseded_by: 150, batch_id: 136, last_import_at: 128,
};
export const spStickyLefts = (run) => {
  const lefts = [];
  let acc = 0;
  run.forEach((c) => { lefts.push(acc); acc += SP_STICKY_WIDTH[c] || 110; });
  return lefts;
};

export default function SPLedgerHeader({
  columns, labels, groups, sort, filters, apiBase, kind, query, onSort, onFilter,
  // Hub View needs the same header against a different endpoint and a different query shape, so the path and
  // the query parameters are arguments now instead of being spelled into the fetch below.
  valuesPath, valueParams,
  // A cell rendered before the sortable columns — Hub View's row-number column, which must not be sortable
  // or filterable because it is a position in the current result, not data.
  leading,
  // And one after them — the Master Sheet's action column, same reasoning: buttons are not data.
  trailing,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);      // { column, anchor }
  const [values, setValues] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);

  const openMenu = (column, event) => {
    event.stopPropagation();
    const r = event.currentTarget.getBoundingClientRect();
    // Open upwards when there is no room below, or the OK row lands off screen and cannot be clicked.
    const MIN_PANEL = 260;
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const up = below < MIN_PANEL && above > below;
    setOpen({
      column,
      anchor: {
        left: Math.round(Math.max(8, Math.min(r.left - 6, window.innerWidth - 250))),
        top: up ? undefined : Math.round(r.bottom + 4),
        bottom: up ? Math.round(window.innerHeight - r.top + 4) : undefined,
        space: Math.round(Math.max(up ? above : below, 200)),
      },
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setValues([]);
    (async () => {
      try {
        const params = new URLSearchParams({ column: open.column });
        if (valueParams) {
          Object.entries(valueParams).forEach(([k, v]) => {
            if (v !== '' && v !== false && v !== null && v !== undefined) params.set(k, v === true ? '1' : String(v));
          });
        } else {
          if (query.search) params.set('search', query.search);
          if (query.missing) params.set('missing', '1');
        }
        if (Object.keys(filters || {}).length) params.set('filters', JSON.stringify(filters));
        const path = valuesPath || `ledger-values/${kind}`;
        const response = await fetch(`${apiBase}/${path}?${params}`, { credentials: 'include' });
        const data = await response.json();
        if (!cancelled && response.ok) { setValues(data.values || []); setTruncated(!!data.truncated); }
      } catch (error) {
        // leave the list empty; the panel says "Loading values…" until this resolves either way
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apiBase, kind, query.search, query.missing, filters, valuesPath, JSON.stringify(valueParams || {})]);

  const run = spStickyRun(columns);
  const lefts = spStickyLefts(run);
  return (
    <tr>
      {leading || null}
      {columns.map((column, index) => {
        const active = sort?.key === column;
        const on = !!(filters && filters[column] && filters[column].length);
        const group = groups?.[column] || 'import';
        // Frozen only until the offsets have actually been measured; positioning before that would place the
        // columns on top of each other for one frame.
        const frozen = index < run.length;
        const isLast = frozen && index === run.length - 1;
        return (
          <th
            key={column}
            className={`sortable-th g-${group} ${frozen ? 'sticky-col' : ''} ${isLast ? 'last-sticky' : ''} `
              + `${active ? `active dir-${sort.dir}` : ''} ${on ? 'filtered' : ''}`}
            // Width pinned here for the same reason the body cells pin theirs: `max-width: 0` plus
            // table-layout:auto lets the browser collapse a frozen cell to its minimum, and then the `left`
            // offsets — which assume the declared widths — put the columns on top of each other.
            style={frozen
              ? { left: lefts[index], width: SP_STICKY_WIDTH[column], minWidth: SP_STICKY_WIDTH[column] }
              : undefined}
          >
            <span
              className="th-label"
              title={t('xHub.ledger.clickSort', { label: labels?.[column] || column })}
              onClick={() => onSort(column, active && sort.dir === 'asc' ? 'desc' : 'asc')}
            >
              {labels?.[column] || column}
            </span>
            <button
              type="button"
              className={`th-funnel ${on ? 'on' : ''}`}
              title={on ? t('xHub.ledger.filteredTo', { n: filters[column].length }) : t('xHub.ledger.sortFilter')}
              onClick={(event) => openMenu(column, event)}
            >
              {/* Excel signals state with the GLYPH, not the colour — a crossed funnel means this column is
                  filtering, which stays legible for anyone who cannot pick the colour out. */}
              {on ? <FilterX size={12} /> : <Filter size={12} />}
            </button>
            {open?.column === column && (
              <FilterPanel
                column={column}
                label={labels?.[column] || column}
                anchor={open.anchor}
                selected={filters?.[column] || []}
                sortDir={active ? sort.dir : null}
                loading={loading}
                values={values}
                truncated={truncated}
                onSort={(dir) => { onSort(column, dir); setOpen(null); }}
                onApply={(picked) => { onFilter(column, picked); setOpen(null); }}
                onClose={() => setOpen(null)}
              />
            )}
          </th>
        );
      })}
      {trailing || null}
    </tr>
  );
}
